/**
 * Checks if an object is a plain object (not an instance of a class).
 *
 * @param obj
 * @returns {boolean}
 */

export function isPlainObject(obj) {
   
    if (typeof obj !== 'object' || obj === null) {
        return false;
    }

    const prototype = Object.getPrototypeOf(obj);
    return prototype === null || prototype === Object.prototype;
}

/**
 * Check if an object has a specific property.
 *
 * @param obj
 * @param propName
 * @returns {boolean}
 */
export function objHasProp(obj, propName) {
    return isPlainObject(obj) && obj.hasOwnProperty(propName);
}

/**
 * Safely gets a value from a nested object using a dot-notation path.
 *
 * @param {object} obj The object to traverse.
 * @param {string} path The dot-notation path (e.g., 'person.employer.name').
 * @returns {any | undefined} The value at the specified path, or undefined if not found.
 */
export function objValue(obj, path) {
   if (typeof obj !== 'object' || obj === null || typeof path !== 'string' || path.length === 0) {
      return undefined;
   }
   
   const keys = path.split('.');
   let current = obj;
   
   for (const key of keys) {
      if (current === undefined || current === null || typeof current !== 'object' || !(key in current)) {
         return undefined;
      }
      current = current[key];
   }
   
   return current;
}

/**
 * Gets a value as a string from a nested object.
 */
export function objValueAsString(obj, path) {
   const value = objValue(obj, path);
   return typeof value === 'string' ? value : '';
}

/**
 * Gets a value as an integer from a nested object.
 */
export function objValueAsInt(obj, path) {
   const value = objValue(obj, path);
   const number = Number(value);
   return Number.isInteger(number) ? number : 0;
}

/**
 * Gets a value as a float from a nested object.
 */
export function objValueAsFloat(obj, path) {
   const value = objValue(obj, path);
   const number = Number(value);
   return Number.isFinite(number) ? number : 0.0;
}

/**
 * Gets a value as an array from a nested object.
 */
export function objValueAsArray(obj, path) {
   const value = objValue(obj, path);
   return Array.isArray(value) ? value : [];
}

/**
 * Gets a value as an object from a nested object.
 */
export function objValueAsObject(obj, path) {
   const value = objValue(obj, path);
   return typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {};
}